<!-- last modified by AI tools on 2026-10-05 -->
<template>
  <button
    type="button"
    :aria-label="t('viewExchangeDetails', { exchanges: tooltipText })"
    v-tooltip.top="isNative ? undefined : tooltipText"
    class="inline-flex w-fit p-0 border-0 bg-transparent"
    @click.stop="handleClick"
  >
    <Avatar
      v-if="exchangeKeys.length === 1"
      :image="logoSrc(exchangeKeys[0])"
      aria-hidden="true"
      shape="circle"
      :pt="avatarPt"
      class="!border-[var(--p-surface-200)] dark:!border-[var(--p-surface-400)] [&>img]:!border-[inherit]"
    />
    <div
      v-else-if="direction === 'vertical' && exchangeKeys.length > 1"
      class="flex flex-col items-center"
      aria-hidden="true"
    >
      <Avatar
        v-for="(ex, i) in visibleExchanges"
        :key="ex"
        :image="logoSrc(ex)"
        shape="circle"
        :pt="avatarPt"
        :class="{ '-mt-1': i > 0 }"
        :style="{ zIndex: visibleExchanges.length - i }"
      />
      <Avatar v-if="overflowCount > 0" :label="`+${overflowCount}`" shape="circle" :pt="overflowPt" class="-mt-1" />
    </div>
    <AvatarGroup
      v-else-if="exchangeKeys.length > 1"
      aria-hidden="true"
      :pt="{ root: 'flex items-center [&>.p-avatar+.p-avatar]:!-ms-3' }"
    >
      <Avatar
        v-for="(ex, i) in visibleExchanges"
        :key="ex"
        :image="logoSrc(ex)"
        shape="circle"
        :pt="avatarPt"
        :style="{ zIndex: visibleExchanges.length - i }"
      />
      <Avatar
        v-if="overflowCount > 0"
        :label="`+${overflowCount}`"
        shape="circle"
        :pt="avatarPt"
        :style="{ backgroundColor: '#e5e7eb', color: '#374151' }"
      />
    </AvatarGroup>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import { getExchangeDisplayNames } from '@/utils/exchangeNames';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    exchange?: string;
    exchanges?: string[];
    max?: number;
    size?: 'default' | 'small';
    single?: boolean;
    direction?: 'horizontal' | 'vertical';
  }>(),
  { max: Infinity, size: 'default', single: false, direction: 'horizontal' },
);

const { t } = useI18n();

const AVATAR_PT_DEFAULT = {
  root: '!w-[20px] !h-[20px] !text-[7px] !font-semibold !border-1 !border-black/10 dark:!border-white/10',
  label: '!text-[7px] !font-semibold',
  image: '!w-full !h-full !object-cover',
};

const AVATAR_PT_SMALL = {
  root: '!w-[20px] !h-[20px] !text-[7px] !font-semibold !border-1 !border-black/10 dark:!border-white/10',
  label: '!text-[7px] !font-semibold',
  image: '!w-full !h-full !object-cover',
};

const OVERFLOW_PT_SMALL = {
  root: '!w-[20px] !h-[20px] !text-[8px] !font-semibold !bg-surface-200 !text-on-surface',
  label: '!text-[8px] !font-semibold',
};

const avatarPt = computed(() => (props.size === 'small' ? AVATAR_PT_SMALL : AVATAR_PT_DEFAULT));
const overflowPt = computed(() => (props.size === 'small' ? OVERFLOW_PT_SMALL : AVATAR_PT_DEFAULT));

function logoSrc(exchange: string): string {
  return `/tws.proxy/public/icons/forecastx?exchange=${exchange.toLowerCase()}`;
}

const FORECASTX_KEYS = ['forecastx', 'forecastex'];

const exchangeKeys = computed(() => {
  let keys: string[] = [];
  if (props.exchanges?.length) keys = [...new Set(props.exchanges)];
  else if (props.exchange) keys = [props.exchange];
  if (keys.length <= 1) return keys;
  const fxKeys = keys.filter((k) => FORECASTX_KEYS.includes(k.toLowerCase()));
  const otherKeys = keys.filter((k) => !FORECASTX_KEYS.includes(k.toLowerCase()));
  return [...fxKeys, ...otherKeys];
});

const visibleExchanges = computed(() => exchangeKeys.value.slice(0, props.max));
const overflowCount = computed(() => Math.max(0, exchangeKeys.value.length - props.max));
const tooltipText = computed(() => getExchangeDisplayNames(exchangeKeys.value));

const isNative = computed(() => app.option?.clientPlatform !== 'web' && app.option?.platform !== 'web');
const supportsDisplayContent = computed(() => !!(app.option as Record<string, unknown>)?.supportsDisplayContentAction);

function handleClick() {
  if (!isNative.value || !supportsDisplayContent.value) return;
  app.send({
    action: 'display_content',
    data: {
      title: 'Exchanges',
      body: `<p>${tooltipText.value}</p>`,
    },
  });
}
</script>

<style>
.p-avatar-group .p-avatar {
  width: 20px !important;
  height: 20px !important;
  border: 1px solid rgb(0 0 0 / 10%) !important;
}

.ratio-dark .p-avatar-group .p-avatar {
  border-color: rgb(255 255 255 / 10%) !important;
}
</style>